import React, { FC } from 'react';
import { Formik } from 'formik';
import cx from 'classnames';

import { useAppContext, usePurchaseContext } from 'QuoteApp/services/providers';

import { SelectorContainer } from '../selectors';
import {
  buildBiweeklyDateSelectorData,
  buildMonthlySelectorData,
  buildSemiMonthlySelectorData,
  buildWeekdaySelectorData,
} from '../utils';
import { validationSchema } from '../schema';
import { PriceContext, PriceCustomizerFormValues, PriceState } from '../domain';
import { PaymentPlanComparisonPanel } from '../PaymentPlanComparisonPanel';
import {
  buildFlexiblePaymentPreferencesPayload,
  getPriceCustomizerInitialValues,
} from '../services';
import styles from './styles.module.css';

export const PricePlanConfigurator: FC = () => {
  const {
    details,
    config,
    rate,
    fetchRate,
    refreshDetails,
    rateLoading,
    detailsLoading,
    api,
  } = useAppContext();
  const { getSignaturePacket } = usePurchaseContext();

  const isLoading = rateLoading || detailsLoading || api.loading;

  const getInitialValues = () =>
    getPriceCustomizerInitialValues({
      details,
      config,
    });

  if (!rate) {
    return null;
  }

  const generateSelectorInitialState = (
    formValues: PriceCustomizerFormValues
  ): {
    status: PriceState;
    context: Partial<PriceContext>;
  } => {
    const contextInitialState = getInitialValues();

    return {
      status: 'ready',
      context: {
        biweeklyDate:
          formValues.biweeklyDate || contextInitialState.biweeklyDate,
        dayOfMonth: formValues.monthly || contextInitialState.monthly,
        dayOfWeek: formValues.dayOfWeek || contextInitialState.dayOfWeek,
        downpayment: 0,
        frequency: formValues.frequency || contextInitialState.frequency,
        semiMonthlyDates:
          formValues.semiMonthlyDates || contextInitialState.semiMonthlyDates,
      },
    };
  };

  return (
    <div className={styles.flexiblePaymentTable}>
      <div className={styles.tableHeader}>
        <h2>Choose a payment plan</h2>
      </div>

      <PaymentPlanComparisonPanel
        rate={rate}
        isLoading={isLoading}
        selectedLabel="Selected"
        onPlanSelect={async (frequencyName) => {
          await api.saveAppData({
            data: buildFlexiblePaymentPreferencesPayload({
              ...getInitialValues(),
              frequency: frequencyName,
            }),
          });
          await fetchRate();
          await refreshDetails();
          getSignaturePacket();
        }}
      >
        {(selectedFrequency) => (
          <div
            className={cx(styles.tableFooter, {
              [styles.hidden]: selectedFrequency === 'fullPay',
            })}
          >
            <div className={styles.footerContent}>
              <Formik
                initialValues={getInitialValues()}
                validationSchema={validationSchema}
                onSubmit={() => {}}
                validateOnMount
                // The button on the PlanComparison makes a selectFrequency call
                // which updates the context state outside of Formik. This allows Formik
                // to reinitialize its values when the frequency changes.
                enableReinitialize
              >
                {({ handleSubmit, values: formValues }) => {
                  return (
                    <form onSubmit={handleSubmit} noValidate>
                      <SelectorContainer
                        isLoading={isLoading}
                        selectorsData={{
                          weekday: buildWeekdaySelectorData(
                            config?.flexiblePayments?.paymentFrequencies
                          ),
                          biweeklyDate: buildBiweeklyDateSelectorData(
                            formValues.dayOfWeek,
                            config?.effectiveDates?.startDate
                          ),
                          semiMonthly: buildSemiMonthlySelectorData(
                            config?.flexiblePayments?.paymentFrequencies
                          ),
                          monthly: buildMonthlySelectorData(
                            config?.flexiblePayments?.paymentFrequencies
                          ),
                        }}
                        initialState={generateSelectorInitialState(formValues)}
                        onSelectorChange={async (props) => {
                          const [[selectorName, value]] = Object.entries(props);

                          await api.saveAppData({
                            data: buildFlexiblePaymentPreferencesPayload({
                              ...formValues,
                              [selectorName as keyof PriceCustomizerFormValues]:
                                value,
                            }),
                          });
                          await fetchRate();
                          await refreshDetails();
                          getSignaturePacket();
                        }}
                      />
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        )}
      </PaymentPlanComparisonPanel>
    </div>
  );
};
